/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module GslbModule */

import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { STORE_TOKEN } from 'ng/root-store/root-store.tokens';
import * as SystemInfoSelectors from 'ng/root-store/system-info/system-info.selectors';

import { Collection } from 'ajs/modules/data-model/factories/collection.factory';
import { isObject } from 'underscore';
import {
    GslbServiceModalComponent,
} from 'ng/modules/gslb/components/gslb-service-modal/gslb-service-modal.component';
import { AviPermissionResource } from 'generated-types';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { GSLB } from 'items/gslb.item.factory';

interface IGslbServiceArgs {
    gslb?: GSLB;
}

const allDataSources = {
    list: {
        source: 'InventoryCollDataSource',
        transformer: 'InventoryDataTransformer',
        transport: 'ListDataTransport',
        fields: ['config', 'runtime', 'health'],
    },
};

export type TGSLBServiceCollection = typeof GSLBServiceCollection;

/**
 * @description
 *
 *     Collection which is supposed to have at most one item. Used to load it or create a new
 *     one using default configuration.
 *
 * @author Alex Malitsky, Ram Pal, Suraj Kumar
 */
export class GSLBServiceCollection extends Collection {
    public static ajsDependencies = [
        'GSLBService',
        STORE_TOKEN,
        'Auth',
    ];

    public readonly gslb: GSLB;
    private readonly auth: Auth;
    private readonly store: Store;

    constructor(args: IGslbServiceArgs = {}) {
        const extendedArgs = {
            objectName: 'gslbservice-inventory',
            allDataSources,
            windowElement: GslbServiceModalComponent,
            isStatic: false,
            permissionName: AviPermissionResource.PERMISSION_GSLBSERVICE,
            ...args,
        };

        super(extendedArgs);

        // expect loaded GSLB item here
        if (isObject(args.gslb)) {
            this.gslb = args.gslb;
        }

        this.serverDefaultsOverride_ = {
            groups: undefined,
            health_monitor_refs: [],
            domain_names: [],
        };

        this.itemClass_ = this.getAjsDependency_('GSLBService');
        this.store = this.getAjsDependency_(STORE_TOKEN);
        this.auth = this.getAjsDependency_('Auth');
    }

    /** @override */
    public isCreatable(): boolean {
        let localSiteIsGslbLeader;

        this.store.select(SystemInfoSelectors.selectLocalSiteIsGslbLeader)
            .pipe(take(1))
            .subscribe(isLeader => localSiteIsGslbLeader = isLeader);

        return localSiteIsGslbLeader && this.auth.isAllowed('gslb') && super.isCreatable();
    }
}
